import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import SubHeader from '../../layout/components/SubHeader';
import TreeView from './TreeView';
import ArticlesList from './ArticlesList';
import ArticlesToolbar from './subparts/ArticlesToolbar';
import ArticleListSkeleton from './subparts/ArticleListSkeleton';
import CategoryTreeSkeleton from './subparts/CategoryTreeSkeleton';
import { getNavIconClass } from '../../../navigation/clinicNavConfig';
import { ARTICLES_PAGE_THEME } from './subparts/articlesCategoryTheme';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import articlesService from '../../../services/articles.service';
import './Articles.css';

function Articles() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [categoriesList, setCategoriesList] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [articlesList, setArticlesList] = useState([]);
    const [articlesLoading, setArticlesLoading] = useState(true);

    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [sortBy, setSortBy] = useState('lastModified');
    const [sortOrder, setSortOrder] = useState('desc');

    const [categoryId, setCategoryId] = useState(0);
    const [categoryTitle, setCategoryTitle] = useState(null);
    const [searchTitle, setSearchTitle] = useState('');
    const [activeSearchTitle, setActiveSearchTitle] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [categoriesOpen, setCategoriesOpen] = useState(true);

    const getHeadingText = () => {
        if (activeSearchTitle) {
            return `Search results for "${activeSearchTitle}"`;
        }
        if (categoryTitle) {
            return categoryTitle;
        }
        return 'All Articles';
    };

    const fetchCategories = useCallback(
        async (options = {}) => {
            const { silent = false } = options;

            if (!clinicId) {
                return;
            }

            if (!silent) {
                setCategoriesLoading(true);
            }

            try {
                const res = await articlesService.getCategories(clinicId);
                setCategoriesList(res.data || []);
            } catch {
                toast.error('Error loading article categories');
            } finally {
                if (!silent) {
                    setCategoriesLoading(false);
                }
            }
        },
        [clinicId]
    );

    const fetchArticles = useCallback(
        async (options = {}) => {
            const { silent = false } = options;

            if (!clinicId) {
                return;
            }

            if (!silent) {
                setArticlesLoading(true);
            }

            try {
                const res = await articlesService.getArticles({
                    clinicId,
                    pageNo,
                    pageSize,
                    sortBy,
                    order: sortOrder,
                    categoryId,
                    title: activeSearchTitle,
                });
                setArticlesList(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
            } catch {
                toast.error('Error loading articles');
            } finally {
                if (!silent) {
                    setArticlesLoading(false);
                }
            }
        },
        [clinicId, pageNo, pageSize, sortBy, sortOrder, categoryId, activeSearchTitle]
    );

    useEffect(() => {
        if (window.matchMedia('(max-width: 991.98px)').matches) {
            setCategoriesOpen(false);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        fetchArticles();
    }, [fetchArticles]);

    const collapseCategoriesOnMobile = () => {
        if (window.matchMedia('(max-width: 991.98px)').matches) {
            setCategoriesOpen(false);
        }
    };

    const handleCategorySelect = (nextCategoryId, nextCategoryTitle) => {
        setCategoryId(nextCategoryId);
        setCategoryTitle(nextCategoryTitle);
        setActiveSearchTitle('');
        setSearchTitle('');
        setPageNo(0);
        collapseCategoriesOnMobile();
    };

    const handleShowAllArticles = () => {
        setCategoryId(0);
        setCategoryTitle(null);
        setActiveSearchTitle('');
        setSearchTitle('');
        setPageNo(0);
        collapseCategoriesOnMobile();
    };

    const handleSearch = (title) => {
        setActiveSearchTitle(title.trim());
        setPageNo(0);
    };

    const handleClearSearch = () => {
        setActiveSearchTitle('');
        setSearchTitle('');
        setPageNo(0);
    };

    const handleSortChange = (nextSortBy, nextSortOrder) => {
        setSortBy(nextSortBy);
        setSortOrder(nextSortOrder);
        setPageNo(0);
    };

    const handlePageSizeChange = (nextPageSize) => {
        setPageSize(nextPageSize);
        setPageNo(0);
    };

    const handlePaginate = (nextPage) => {
        setPageNo(nextPage);
    };

    const handleRefresh = async () => {
        if (!clinicId || isRefreshing) {
            return;
        }

        setIsRefreshing(true);

        try {
            await Promise.all([
                fetchArticles({ silent: true }),
                fetchCategories({ silent: true }),
            ]);
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <div className="ecx-page articles-page">
            <SubHeader
                title="Articles"
                description={`Browse and share educational content with patients${
                    totalElements ? ` · ${totalElements} total` : ''
                }`}
                icon="bi-file-text"
                iconTheme={ARTICLES_PAGE_THEME}
                containerClass="container-fluid px-4"
            >
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm rounded-pill"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    title="Refresh articles"
                >
                    <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`}></i>
                    Refresh
                </button>
            </SubHeader>

            <div className="container-fluid px-4 py-3">
                <div className="row g-3">
                    <div className="col-12 col-lg-3 col-xl-3 order-2 order-lg-1">
                        <div
                            className={`ecx-page-card ecx-page-panel articles-category-tree${
                                categoriesOpen ? '' : ' articles-category-tree--collapsed'
                            }`}
                        >
                            <div className="ecx-page-panel__header articles-category-tree__header">
                                <button
                                    type="button"
                                    className="articles-category-tree__toggle d-lg-none"
                                    onClick={() => setCategoriesOpen((open) => !open)}
                                    aria-expanded={categoriesOpen}
                                    aria-controls="articles-category-tree-body"
                                >
                                    <div className="d-flex align-items-center gap-2 min-width-0">
                                        <span
                                            className={`articles-category-item__icon ecx-nav-icon ${getNavIconClass(ARTICLES_PAGE_THEME)}`}
                                            style={{ width: '2rem', height: '2rem' }}
                                        >
                                            <i className="bi bi-diagram-3"></i>
                                        </span>
                                        <div className="text-start min-width-0">
                                            <h6 className="mb-0 fw-normal">Categories</h6>
                                            {!categoriesOpen && categoryTitle && (
                                                <span className="articles-category-tree__active small text-muted text-truncate d-block">
                                                    {categoryTitle}
                                                </span>
                                            )}
                                            {!categoriesOpen && !categoryTitle && (
                                                <span className="articles-category-tree__active small text-muted d-block">
                                                    All Articles
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <i
                                        className={`bi bi-chevron-${categoriesOpen ? 'up' : 'down'} articles-category-tree__chevron`}
                                    ></i>
                                </button>

                                <div className="d-none d-lg-flex align-items-center gap-2">
                                    <span
                                        className={`articles-category-item__icon ecx-nav-icon ${getNavIconClass(ARTICLES_PAGE_THEME)}`}
                                        style={{ width: '2rem', height: '2rem' }}
                                    >
                                        <i className="bi bi-diagram-3"></i>
                                    </span>
                                    <h6 className="mb-0 fw-normal">Categories</h6>
                                </div>
                            </div>
                            <div
                                id="articles-category-tree-body"
                                className="ecx-page-panel__body ecx-page-scroll articles-category-tree__body"
                            >
                                {categoriesLoading ? (
                                    <CategoryTreeSkeleton count={4} />
                                ) : (
                                    <TreeView
                                        categories={categoriesList}
                                        selectedCategoryId={categoryId || null}
                                        onSelectCategory={handleCategorySelect}
                                        onShowAll={handleShowAllArticles}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-lg-9 col-xl-9 order-1 order-lg-2">
                        <div className="ecx-page-card ecx-page-panel articles-list-panel">
                            <div className="ecx-page-panel__body d-flex flex-column">
                                <ArticlesToolbar
                                    searchTerm={searchTitle}
                                    onSearchTermChange={setSearchTitle}
                                    onSearch={handleSearch}
                                    onClearSearch={handleClearSearch}
                                    sortBy={sortBy}
                                    sortOrder={sortOrder}
                                    onSortChange={handleSortChange}
                                    totalElements={totalElements}
                                    headingText={getHeadingText()}
                                />

                                {articlesLoading ? (
                                    <ArticleListSkeleton count={pageSize > 5 ? 5 : pageSize} />
                                ) : (
                                    <ArticlesList
                                        articlesList={articlesList}
                                        currentPage={pageNo}
                                        totalPages={totalPages}
                                        totalElements={totalElements}
                                        pageSize={pageSize}
                                        onPageChange={handlePaginate}
                                        onPageSizeChange={handlePageSizeChange}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Articles;
